import type { MoveOutCharges } from '../utils/portvilleMoveOutCharges';

// 호실 기본 정보 (portville_rooms 컬렉션)
export interface PortvilleRoom {
  id: string;
  roomNumber: string; // 호실번호 (예: "101", "102") 또는 시설물 코드 (예: "EV-001")
  roomType: '호실' | '시설물'; // 호실 또는 시설물 구분
  status: 'occupied' | 'preparing' | 'vacant' | 'contracting' | 'facility'; // 입주중, 준비중, 공실, 계약진행, 시설물
  currentResidencyId?: string; // 현재 입주 기록 ID (portville_residencies 컬렉션 참조)

  // 계약/입주 대상자 정보 (입주 기록이 생성되기 전 단계에서 UI 표시용)
  currentResident?: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  residentUid?: string;
  
  // 시설물 정보 (시설물인 경우에만)
  facilityInfo?: {
    facilityType: '전기차 충전소' | '통신 중계기' | '기타';
    facilityName: string;
    responsiblePerson: {
      name: string;
      phoneNumber: string;
      organization: string;
    };
  };
  
  createdAt: Date;
  updatedAt: Date;
}

export interface PortvilleRoommateAttachment {
  id: string;
  name: string;
  storagePath: string;
  size: number;
  contentType: string;
  uploadedAt?: Date;
}

export interface PortvilleRoommate {
  name: string;
  contact: string;
  attachment?: PortvilleRoommateAttachment;
}

export interface PortvilleRoommateFormData extends PortvilleRoommate {
  pendingAttachmentFile?: File;
}

// 입주 기록 정보 (portville_residencies 컬렉션)
export interface PortvilleResidency {
  id: string;
  roomId: string; // 호실 ID (portville_rooms 컬렉션 참조)
  roomNumber: string; // 호실번호 (검색 편의를 위한 중복 저장)
  residentUid: string; // 입주자 사용자 ID (Firebase Auth UID)
  
  // 입주자 기본 정보
  residentInfo: {
    name: string; // 성명
    phoneNumber: string; // 연락처
    organization: string; // 소속
  };
  
  // 입주 기간
  occupancyPeriod?: {
    startDate: Date; // 입주 개시일
    endDate: Date; // 계약 종료일
  };
  
  // 관리자 추가 정보
  roommateCount?: 0 | 1 | 2 | 3;
  roommateInfo?: string; // 동거인 1 성명 (기존 필드)
  roommateContact?: string; // 동거인 1 연락처 (기존 필드)
  roommateAttachment?: PortvilleRoommateAttachment;
  roommate2Info?: string;
  roommate2Contact?: string;
  roommate2Attachment?: PortvilleRoommateAttachment;
  roommate3Info?: string;
  roommate3Contact?: string;
  roommate3Attachment?: PortvilleRoommateAttachment;
  vehicleInfo?: string; // 차량정보
  notes?: string; // 비고
  
  // 계약 관련 정보
  signatureName?: string; // 서명자 성명
  signatureAddress?: string; // 서명자 주소
  signatureResidentNumber?: string; // 서명자 주민등록번호
  signatureDate?: Date; // 서명 날짜
  signatureUrl?: string; // 서명 이미지 URL
  contractSignedAt?: Date; // 계약 날인 완료 시간
  moveInInspectionSignedAt?: Date; // 입주점검 완료 시간
  moveInInspectionSignatureUrl?: string; // 입주점검 서명 이미지 URL
  moveOutInspectionSignedAt?: Date; // 퇴거점검 완료 시간
  moveOutInspectionId?: string; // 개별 퇴거점검 기록 ID
  moveOutInspectionInspectorName?: string; // 퇴거점검 점검자명
  moveOutInspectionSignatureUrl?: string; // 퇴거점검 서명 이미지 URL
  moveOutInspectionNotes?: string; // 퇴거점검 메모
  moveOutInspectionCharges?: MoveOutCharges; // 점검 당시 금액 및 입금계좌
  landlordSignedContractUrl?: string; // 임대인 날인본 계약서 URL
  landlordSignedContractUploadedAt?: Date; // 임대인 날인본 업로드 시간

  // 계약 내용
  contractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  };

  // 계약 연장 관련 정보
  hasExtended?: boolean; // 계약 연장 여부 (기본값: false)
  extensionStatus?: 'none' | 'applied' | 'approved' | 'rejected' | 'contracted'; // 연장 신청 상태
  extensionAppliedAt?: Date; // 계약 연장 신청일
  extensionApprovedAt?: Date; // 계약 연장 승인일
  extensionRejectedAt?: Date; // 계약 연장 거부일
  extensionRejectionReason?: string; // 계약 연장 거부 사유
  originalEndDate?: Date; // 원래 계약 종료일 (연장 전)

  // 계약 연장 신청 첨부 서류 (사용자 업로드)
  extensionDocuments?: Partial<Record<'identification' | 'employment' | 'other', string>>;
  extensionDocumentsUpdatedAt?: Date;
  
  // 계약 연장 날인 정보
  extensionSignatureName?: string; // 연장 계약 서명자 성명
  extensionSignatureDate?: Date; // 연장 계약 서명 날짜
  extensionSignatureUrl?: string; // 연장 계약 서명 이미지 URL
  extensionContractSignedAt?: Date; // 연장 계약 날인 완료 시간
  extensionLandlordSignedContractUrl?: string; // 연장계약 임대인 날인본 계약서 URL
  extensionLandlordSignedContractUploadedAt?: Date; // 연장계약 임대인 날인본 업로드 시간
  extensionContractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  }; // 연장 계약서 내용

  // 2차 계약 연장 관련 정보
  hasSecondExtended?: boolean; // 2차 계약 연장 여부
  secondExtensionStatus?: 'none' | 'applied' | 'approved' | 'rejected' | 'contracted'; // 2차 연장 신청 상태
  secondExtensionAppliedAt?: Date; // 2차 계약 연장 신청일
  secondExtensionApprovedAt?: Date; // 2차 계약 연장 승인일
  secondExtensionRejectedAt?: Date; // 2차 계약 연장 거부일
  secondExtensionRejectionReason?: string; // 2차 계약 연장 거부 사유
  secondOriginalEndDate?: Date; // 2차 연장 전 계약 종료일 (1차 연장 후 종료일)

  // 2차 계약 연장 신청 첨부 서류 (사용자 업로드)
  secondExtensionDocuments?: Partial<Record<'identification' | 'employment' | 'other', string>>;
  secondExtensionDocumentsUpdatedAt?: Date;

  // 2차 계약 연장 날인 정보
  secondExtensionSignatureName?: string; // 2차 연장 계약 서명자 성명
  secondExtensionSignatureDate?: Date; // 2차 연장 계약 서명 날짜
  secondExtensionSignatureUrl?: string; // 2차 연장 계약 서명 이미지 URL
  secondExtensionContractSignedAt?: Date; // 2차 연장 계약 날인 완료 시간
  secondExtensionLandlordSignedContractUrl?: string; // 2차 연장계약 임대인 날인본 계약서 URL
  secondExtensionLandlordSignedContractUploadedAt?: Date; // 2차 연장계약 임대인 날인본 업로드 시간
  secondExtensionContractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  }; // 2차 연장 계약서 내용

  // 퇴거 관련 정보 (퇴거신청 승인 시 추가)
  moveOutInfo?: {
    originalEndDate: Date; // 원래 계약 종료일
    approvedMoveOutDate: Date; // 승인된 퇴거일
    moveOutReason: 'personnel_change' | 'personal_reason'; // 퇴거 사유
    approvedAt: Date; // 퇴거 승인일
    approvedBy: string; // 승인한 관리자 UID
    notes?: string; // 승인 메모
  };
  
  // 상태 및 날짜 정보
  status: 'contracting' | 'active' | 'completed' | 'terminated'; // 계약진행중, 입주중, 정상완료, 중도해지
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date; // 입주 완료 날짜 (퇴실 완료 시)
}

// 폼 데이터 타입 (기존 호환성 유지를 위해 유지하되 내부적으로 분리 처리)
export interface PortvilleRoomFormData {
  roomNumber: string;
  roomType: '호실' | '시설물';
  status: 'occupied' | 'preparing' | 'vacant' | 'contracting' | 'facility';
  residentUid?: string;
  residentName?: string;
  residentPhoneNumber?: string;
  residentOrganization?: string;
  startDate?: string;
  endDate?: string;
  hasManualExtension1?: boolean;
  originalContractEndDate?: string;
  roommates?: PortvilleRoommateFormData[];
  roommateInfo?: string; // 기존 호출부 호환
  roommateContact?: string; // 기존 호출부 호환
  vehicleInfo?: string; // 차량정보
  notes?: string; // 비고
}

// 시설물 생성 폼 데이터 타입
export interface FacilityFormData {
  facilityCode: string;        // "EV-001", "COMM-001" 등
  facilityName: string;        // "전기차 충전소 1호기"
  facilityType: '전기차 충전소' | '통신 중계기' | '기타';
  responsiblePerson: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
}

// 통합 뷰용 타입 (UI에서 사용)
export interface PortvilleRoomWithResidency {
  // 호실 정보
  id: string;
  roomNumber: string;
  roomType: '호실' | '시설물';
  status: 'occupied' | 'preparing' | 'vacant' | 'contracting' | 'facility';
  createdAt: Date;
  updatedAt: Date;
  
  // 시설물 정보 (시설물인 경우에만)
  facilityInfo?: {
    facilityType: '전기차 충전소' | '통신 중계기' | '기타';
    facilityName: string;
    responsiblePerson: {
      name: string;
      phoneNumber: string;
      organization: string;
    };
  };
  
  // 현재 입주자 정보 (있는 경우)
  currentResident?: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  residentUid?: string; // 입주자 사용자 ID
  occupancyPeriod?: {
    startDate: Date;
    endDate: Date;
  };
  roommateInfo?: string;
  roommateContact?: string;
  roommateCount?: 0 | 1 | 2 | 3;
  roommateAttachment?: PortvilleRoommateAttachment;
  roommate2Info?: string;
  roommate2Contact?: string;
  roommate2Attachment?: PortvilleRoommateAttachment;
  roommate3Info?: string;
  roommate3Contact?: string;
  roommate3Attachment?: PortvilleRoommateAttachment;
  roommates?: PortvilleRoommate[];
  vehicleInfo?: string;
  notes?: string;
  signatureName?: string;
  signatureAddress?: string;
  signatureResidentNumber?: string;
  signatureDate?: Date;
  signatureUrl?: string;
  contractSignedAt?: Date;
  moveInInspectionSignedAt?: Date;
  moveInInspectionSignatureUrl?: string;
  moveOutInspectionSignedAt?: Date;
  moveOutInspectionId?: string;
  moveOutInspectionInspectorName?: string;
  moveOutInspectionSignatureUrl?: string;
  moveOutInspectionNotes?: string;
  moveOutInspectionCharges?: MoveOutCharges;
  landlordSignedContractUrl?: string;
  landlordSignedContractUploadedAt?: Date;

  // 계약 내용
  contractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  };

  // 계약 연장 관련 정보
  hasExtended?: boolean; // 계약 연장 여부
  extensionStatus?: 'none' | 'applied' | 'approved' | 'rejected' | 'contracted'; // 연장 신청 상태
  extensionAppliedAt?: Date; // 계약 연장 신청일
  extensionApprovedAt?: Date; // 계약 연장 승인일
  extensionRejectedAt?: Date; // 계약 연장 거부일
  extensionRejectionReason?: string; // 계약 연장 거부 사유
  originalEndDate?: Date; // 원래 계약 종료일 (연장 전)

  // 계약 연장 신청 첨부 서류 (사용자 업로드)
  extensionDocuments?: Partial<Record<'identification' | 'employment' | 'other', string>>;
  extensionDocumentsUpdatedAt?: Date;
  
  // 계약 연장 날인 정보 (PortvilleRoomWithResidency에 추가)
  extensionSignatureName?: string; // 연장 계약 서명자 성명
  extensionSignatureDate?: Date; // 연장 계약 서명 날짜
  extensionSignatureUrl?: string; // 연장 계약 서명 이미지 URL
  extensionContractSignedAt?: Date; // 연장 계약 날인 완료 시간
  extensionLandlordSignedContractUrl?: string; // 연장계약 임대인 날인본 계약서 URL
  extensionLandlordSignedContractUploadedAt?: Date; // 연장계약 임대인 날인본 업로드 시간
  extensionContractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  }; // 연장 계약서 내용

  // 2차 계약 연장 관련 정보
  hasSecondExtended?: boolean; // 2차 계약 연장 여부
  secondExtensionStatus?: 'none' | 'applied' | 'approved' | 'rejected' | 'contracted'; // 2차 연장 신청 상태
  secondExtensionAppliedAt?: Date; // 2차 계약 연장 신청일
  secondExtensionApprovedAt?: Date; // 2차 계약 연장 승인일
  secondExtensionRejectedAt?: Date; // 2차 계약 연장 거부일
  secondExtensionRejectionReason?: string; // 2차 계약 연장 거부 사유
  secondOriginalEndDate?: Date; // 2차 연장 전 계약 종료일 (1차 연장 후 종료일)

  // 2차 계약 연장 신청 첨부 서류 (사용자 업로드)
  secondExtensionDocuments?: Partial<Record<'identification' | 'employment' | 'other', string>>;
  secondExtensionDocumentsUpdatedAt?: Date;

  // 2차 계약 연장 날인 정보
  secondExtensionSignatureName?: string; // 2차 연장 계약 서명자 성명
  secondExtensionSignatureDate?: Date; // 2차 연장 계약 서명 날짜
  secondExtensionSignatureUrl?: string; // 2차 연장 계약 서명 이미지 URL
  secondExtensionContractSignedAt?: Date; // 2차 연장 계약 날인 완료 시간
  secondExtensionLandlordSignedContractUrl?: string; // 2차 연장계약 임대인 날인본 계약서 URL
  secondExtensionLandlordSignedContractUploadedAt?: Date; // 2차 연장계약 임대인 날인본 업로드 시간
  secondExtensionContractContent?: {
    header?: string;
    signerInfo?: {
      name: string;
      address: string;
      residentNumber: string;
    };
    article1: string;
    article2: string;
    article3: string;
    article4: string;
    article5: string;
    article6?: string;
    article7?: string;
    article8?: string;
    article9?: string;
    article10?: string;
    article11?: string;
    footer?: string;
  }; // 2차 연장 계약서 내용

  // 입주 기록 ID
  currentResidencyId?: string;
  
  // 퇴거 관련 정보
  moveOutInfo?: {
    originalEndDate: Date; // 원래 계약 종료일
    approvedMoveOutDate: Date; // 승인된 퇴거일
    moveOutReason: 'personnel_change' | 'personal_reason'; // 퇴거 사유
    approvedAt: Date; // 퇴거 승인일
    approvedBy: string; // 승인한 관리자 UID
    notes?: string; // 승인 메모
  };
  
  // 퇴거신청 관련 정보
  hasPendingMoveOut?: boolean; // 대기중인 퇴거신청 여부
  moveOutRequest?: any; // 퇴거신청 데이터 (상세 타입은 나중에 정의)

  // 계약 전 포기신청 관련 정보
  pendingContractCancellation?: {
    lotteryId: string;
    requestedAt?: Date;
    requestedByUid?: string;
    reason?: string;
  };
}

export const ROOM_STATUS_LABELS = {
  occupied: '입주중',
  preparing: '준비중',
  vacant: '공실',
  contracting: '계약진행',
  facility: '시설물'
} as const;

export type RoomStatusFilter =
  | 'all'
  | 'occupied'
  | 'preparing'
  | 'vacant'
  | 'contracting'
  | 'facility'
  | 'moveout-pending'
  | 'contract-cancellation-pending'
  | 'extension-pending';

export const ROOM_STATUS_COLORS = {
  occupied: 'bg-red-100 text-red-800',
  preparing: 'bg-yellow-100 text-yellow-800',
  vacant: 'bg-green-100 text-green-800',
  contracting: 'bg-blue-100 text-blue-800',
  facility: 'bg-purple-100 text-purple-800'
} as const;

export const ROOM_TYPE_LABELS = {
  '호실': '호실',
  '시설물': '시설물'
} as const;

export const FACILITY_TYPE_LABELS = {
  '전기차 충전소': '전기차 충전소',
  '통신 중계기': '통신 중계기',
  '기타': '기타'
} as const;

export const RESIDENCY_STATUS_LABELS = {
  contracting: '계약진행중',
  active: '입주중',
  completed: '정상완료',
  terminated: '중도해지'
} as const; 
